"use client";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setShowChat,
  setShowPolls,
  setShowNotes,
  selectShowChat,
  setShowSettings,
  selectShowNotes,
  selectShowPolls,
  setShowWhiteboard,
  selectShowSettings,
  setShowParticipants,
  selectShowWhiteboard,
  setShowMeetingDetails,
  selectShowParticipants,
  selectShowMeetingDetails
} from "@client/store/slices/ui.slice";

interface UseUIStateReturn {
  showChat: boolean;
  showParticipants: boolean;
  showSettings: boolean;
  showPolls: boolean;
  showWhiteboard: boolean;
  showNotes: boolean;
  toggleChat: () => void;
  toggleParticipants: () => void;
  toggleSettings: () => void;
  togglePolls: () => void;
  toggleWhiteboard: () => void;
  toggleNotes: () => void;
  closeChat: () => void;
  closeParticipants: () => void;
  closePolls: () => void;
  closeWhiteboard: () => void;
  closeNotes: () => void;
  closeMeetingDetails: () => void;
  showMeetingDetails: boolean;
  toggleMeetingDetails: () => void;
}

export const useUIState = (): UseUIStateReturn => {
  const dispatch = useDispatch();

  // Read state from Redux store
  const showChat = useSelector(selectShowChat);
  const showParticipants = useSelector(selectShowParticipants);
  const showSettings = useSelector(selectShowSettings);
  const showPolls = useSelector(selectShowPolls);
  const showWhiteboard = useSelector(selectShowWhiteboard);
  const showNotes = useSelector(selectShowNotes);
  const showMeetingDetails = useSelector(selectShowMeetingDetails);

  // Helper to close all sidebars
  const closeAll = useCallback(() => {
    dispatch(setShowChat(false));
    dispatch(setShowParticipants(false));
    dispatch(setShowPolls(false));
    dispatch(setShowSettings(false));
    dispatch(setShowNotes(false));
    dispatch(setShowWhiteboard(false));
    dispatch(setShowMeetingDetails(false));
  }, [dispatch]);

  // Toggle functions - close all others when opening one
  const toggleChat = useCallback(() => {
    const newValue = !showChat;
    closeAll();
    dispatch(setShowChat(newValue));
  }, [dispatch, showChat, closeAll]);

  const toggleParticipants = useCallback(() => {
    const newValue = !showParticipants;
    closeAll();
    dispatch(setShowParticipants(newValue));
  }, [dispatch, showParticipants, closeAll]);

  const toggleSettings = useCallback(() => {
    const newValue = !showSettings;
    closeAll();
    dispatch(setShowSettings(newValue));
  }, [dispatch, showSettings, closeAll]);

  const togglePolls = useCallback(() => {
    const newValue = !showPolls;
    closeAll();
    dispatch(setShowPolls(newValue));
  }, [dispatch, showPolls, closeAll]);

  const toggleWhiteboard = useCallback(() => {
    const newValue = !showWhiteboard;
    closeAll();
    dispatch(setShowWhiteboard(newValue));
  }, [dispatch, showWhiteboard, closeAll]);

  const toggleNotes = useCallback(() => {
    const newValue = !showNotes;
    closeAll();
    dispatch(setShowNotes(newValue));
  }, [dispatch, showNotes, closeAll]);

  const toggleMeetingDetails = useCallback(() => {
    const newValue = !showMeetingDetails;
    closeAll();
    dispatch(setShowMeetingDetails(newValue));
  }, [dispatch, showMeetingDetails, closeAll]);

  // Close functions
  const closeChat = useCallback(() => {
    dispatch(setShowChat(false));
  }, [dispatch]);

  const closeParticipants = useCallback(() => {
    dispatch(setShowParticipants(false));
  }, [dispatch]);

  const closePolls = useCallback(() => {
    dispatch(setShowPolls(false));
  }, [dispatch]);

  const closeWhiteboard = useCallback(() => {
    dispatch(setShowWhiteboard(false));
  }, [dispatch]);

  const closeNotes = useCallback(() => {
    dispatch(setShowNotes(false));
  }, [dispatch]);

  const closeMeetingDetails = useCallback(() => {
    dispatch(setShowMeetingDetails(false));
  }, [dispatch]);

  return useMemo(
    () => ({
      showChat,
      showParticipants,
      showSettings,
      showPolls,
      showWhiteboard,
      showNotes,
      showMeetingDetails,
      toggleChat,
      toggleParticipants,
      toggleSettings,
      togglePolls,
      toggleWhiteboard,
      toggleNotes,
      toggleMeetingDetails,
      closeChat,
      closeParticipants,
      closePolls,
      closeWhiteboard,
      closeNotes,
      closeMeetingDetails
    }),
    [
      showChat,
      showParticipants,
      showSettings,
      showPolls,
      showWhiteboard,
      showNotes,
      showMeetingDetails,
      toggleChat,
      toggleParticipants,
      toggleSettings,
      togglePolls,
      toggleWhiteboard,
      toggleNotes,
      toggleMeetingDetails,
      closeChat,
      closeParticipants,
      closePolls,
      closeWhiteboard,
      closeNotes,
      closeMeetingDetails
    ]
  );
};
